<template>
  <Transition
    enter-active-class="transform transition ease-out duration-300"
    enter-from-class="translate-y-[-100%] opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transform transition ease-in duration-200"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-[-100%] opacity-0"
  >
    <div
      v-if="hasUpdate"
      class="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
    >
      <div class="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between flex-wrap gap-4">
          <!-- Left: Icon and Message -->
          <div class="flex items-center gap-3">
            <div class="flex-shrink-0">
              <svg 
                class="h-6 w-6 text-white animate-pulse" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  stroke-width="2" 
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                />
              </svg>
            </div>
            <div class="flex-1">
              <p class="font-medium text-sm sm:text-base">
                {{ $t('version.updateAvailable') || 'A new version is available!' }}
              </p>
              <p class="text-indigo-100 text-xs sm:text-sm mt-0.5">
                {{ $t('version.updateDescription') || 'Refresh to get the latest features and improvements.' }}
              </p>
            </div>
          </div>

          <!-- Right: Actions -->
          <div class="flex items-center gap-2 sm:gap-3">
            <!-- Dismiss Button -->
            <button
              @click="dismissUpdate"
              class="px-3 py-1.5 text-sm font-medium text-indigo-100 hover:text-white 
                     bg-white/10 hover:bg-white/20 rounded-lg transition-colors
                     focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              {{ $t('version.later') || 'Later' }}
            </button>

            <!-- Refresh Button -->
            <button
              @click="refreshApp"
              class="px-4 py-1.5 text-sm font-medium text-indigo-600 
                     bg-white hover:bg-indigo-50 rounded-lg shadow-sm
                     transition-all hover:shadow-md active:scale-95
                     focus:outline-none focus:ring-2 focus:ring-white/50
                     flex items-center gap-2"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {{ $t('version.refreshNow') || 'Refresh Now' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Progress bar showing auto-refresh countdown -->
      <div 
        v-if="autoRefreshCountdown > 0"
        class="h-1 bg-white/20"
      >
        <div 
          class="h-full bg-white transition-all duration-1000 ease-linear"
          :style="{ width: `${(autoRefreshCountdown / 60) * 100}%` }"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onUnmounted, watch } from 'vue';
import { useVersionCheck } from '@/composables/useVersionCheck';

const { hasUpdate, dismissUpdate, refreshApp } = useVersionCheck({
  checkInterval: 60000, // Check every minute
  enableInDev: false
});

// Auto-refresh countdown (optional feature)
const autoRefreshCountdown = ref(0);
const AUTO_REFRESH_DELAY = 60; // seconds
let countdownInterval: ReturnType<typeof setInterval> | null = null;

// Watch for update and start countdown
watch(hasUpdate, (newValue) => {
  if (newValue) {
    // Start auto-refresh countdown
    autoRefreshCountdown.value = AUTO_REFRESH_DELAY;
    countdownInterval = setInterval(() => {
      autoRefreshCountdown.value--;
      if (autoRefreshCountdown.value <= 0) {
        refreshApp();
      }
    }, 1000);
  } else {
    // Clear countdown
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
    autoRefreshCountdown.value = 0;
  }
});

// Clean up
onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});
</script>

<style scoped>
/* Optional: Add slide animation for mobile */
@media (max-width: 640px) {
  .fixed {
    position: fixed;
  }
}
</style>
